// src/pages/InventoryList.js
import React, { useState, useEffect, useContext } from 'react';
import { Typography, Paper, Button, Box, CircularProgress, Table, TableHead, TableBody, TableRow, TableCell, Checkbox } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TanStackTable from '../components/TanStackTable';
import BulkActionResults from '../components/BulkActionResults';
import { getInventoryItemsByOrgId, deleteMultipleInventoryItems } from '../services/inventoryService';
import { OrgContext } from '../contexts/OrgContext'; // Import OrgContext
import Papa from 'papaparse'; // Add this import

const InventoryList = () => {
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { orgId } = useContext(OrgContext); // Access orgId from OrgContext
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState([]);
  const [deleteResults, setDeleteResults] = useState(null);
  
  // Pagination and filtering state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('product.product_name');
  const [sortOrder, setSortOrder] = useState('ASC');

  const fetchInventory = React.useCallback(async (customOptions = {}) => {
    if (!orgId) return;
    setLoading(true);
    try {
      const requestOptions = {
        page: customOptions.page !== undefined ? customOptions.page : pagination.page,
        limit: customOptions.limit !== undefined ? customOptions.limit : pagination.limit,
        search: customOptions.search !== undefined ? customOptions.search : searchQuery,
        sortBy: customOptions.sortBy !== undefined ? customOptions.sortBy : sortBy,
        sortOrder: customOptions.sortOrder !== undefined ? customOptions.sortOrder : sortOrder
      };

      const response = await getInventoryItemsByOrgId(orgId, requestOptions);
      
      // Check if response is paginated
      if (response.data && Array.isArray(response.data)) {
        setRowData(response.data);
        setPagination(response.pagination);
      } else if (Array.isArray(response)) {
        // Fallback for non-paginated response
        setRowData(response);
      }
    } catch (error) {
      console.error('Error fetching inventory:', error);
    } finally {
      setLoading(false);
    }
  }, [orgId, pagination.page, pagination.limit, searchQuery, sortBy, sortOrder]);

  useEffect(() => {
    if (orgId) {
      fetchInventory();
    }
  }, [orgId, fetchInventory]);

  const handleAddInventory = () => {
    navigate(`/${orgId}/inventory/add`);
  };

  const handleDownload = () => {
    // Transform the data for CSV export
    const exportData = rowData.map(item => ({
      'Product ID': item.product.id,
      'Product Name': item.product.product_name,
      'Category': item.product.category,
      'Sub Category': item.product.sub_category,
      'Quantity On Hand': item.quantity_on_hand,
      'Minimum Stock Level': item.minimum_stock_level,
      'Maximum Stock Level': item.maximum_stock_level,
      'Reorder Point': item.reorder_point,
      'Excess Inventory': item.excess_inventory ? 'Yes' : 'No',
      'Last Unit Cost': item.last_unit_cost,
      'Last Unit Price': item.last_unit_price,
      'Unit of Measure': item.product.unit_of_measure,
      'Company': item.product.company,
      'SKU': item.product.sku
    }));

    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const now = new Date();
    const isoDate = now.toISOString().slice(0, 10); // yyyy-mm-dd format
    const uniqueId = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}${String(now.getMilliseconds()).padStart(3, '0')}`;
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `${isoDate}_inventory_${uniqueId}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteClick = () => {
    setDeleteMode(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await deleteMultipleInventoryItems(selectedInventory);
      setDeleteResults(response);
      await fetchInventory(); // Refresh the list
    } catch (error) {
      console.error('Error deleting inventory items:', error);
    } finally {
      setDeleteMode(false);
      setSelectedInventory([]);
    }
  };

  const handleCancelDelete = () => {
    setDeleteMode(false);
    setSelectedInventory([]);
    setDeleteResults(null);
  };

  const handleCloseResults = () => {
    setDeleteResults(null);
  };

  const columns = [
    {
      accessorKey: 'product.id',
      header: 'Product ID',
      accessorFn: (row) => row.product?.id || '',
    },
    {
      accessorKey: 'product.product_name',
      header: 'Product Name',
      accessorFn: (row) => row.product?.product_name || '',
    },
    {
      accessorKey: 'product.category',
      header: 'Category',
      accessorFn: (row) => row.product?.category || '',
    },
    {
      accessorKey: 'product.sub_category',
      header: 'Sub Category',
      accessorFn: (row) => row.product?.sub_category || '',
    },
    {
      accessorKey: 'quantity_on_hand',
      header: 'Quantity On Hand',
    },
    {
      accessorKey: 'minimum_stock_level',
      header: 'Minimum Stock Level',
    },
    {
      accessorKey: 'maximum_stock_level',
      header: 'Maximum Stock Level',
    },
    {
      accessorKey: 'reorder_point',
      header: 'Reorder Point',
    },
    {
      accessorKey: 'excess_inventory',
      header: 'Excess Inventory',
      cell: ({ getValue }) => getValue() ? 'Yes' : 'No',
    },
  ];

  const getRowClassName = (row) => {
    const { quantity_on_hand, minimum_stock_level, reorder_point, excess_inventory } = row;
    if (excess_inventory || quantity_on_hand < minimum_stock_level) {
      return { 
        backgroundColor: 'rgba(229, 57, 53, 0.22)', // error tint
        color: 'inherit',
        '& .MuiTableCell-root': {
          backgroundColor: 'rgba(229, 57, 53, 0.22)',
          color: 'inherit',
        }
      };
    }
    if (quantity_on_hand < reorder_point) {
      return { 
        backgroundColor: 'rgba(245, 124, 0, 0.22)', // warning tint
        color: 'inherit',
        '& .MuiTableCell-root': {
          backgroundColor: 'rgba(245, 124, 0, 0.22)',
          color: 'inherit',
        }
      };
    }
    return {
      backgroundColor: 'rgba(0,0,0,0)',
      color: 'inherit',
      '& .MuiTableCell-root': {
        backgroundColor: 'rgba(0,0,0,0)',
        color: 'inherit',
      }
    };
  };

  const handleRowClick = (row) => {
    navigate(`/${orgId}/inventory/${row.product.id}`);
  };

  return (
    <Paper sx={{ p: 2, bgcolor: 'background.paper', color: 'text.primary', backdropFilter: 'saturate(140%) blur(6px)', border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="h5" gutterBottom>
        Inventory
      </Typography>

      {deleteResults && (
        <BulkActionResults 
          results={deleteResults} 
          actionType="deletion"
          onClose={handleCloseResults}
        />
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        {!deleteMode ? (
          <>
            <Button variant="contained" onClick={handleAddInventory}>
              Add Inventory
            </Button>
            <Button variant="contained" onClick={() => navigate(`/${orgId}/inventory/bulk-import`)}>
              Add Inventory in Bulk
            </Button>
            <Button variant="contained" onClick={handleDownload}>
              Download Inventory List
            </Button>
            <Button variant="contained" color="error" onClick={handleDeleteClick}>
              Delete Inventory Items
            </Button>
          </>
        ) : (
          <>
            <Button variant="contained" color="error" onClick={handleConfirmDelete}>
              Confirm Delete
            </Button>
            <Button variant="contained" onClick={handleCancelDelete}>
              Cancel
            </Button>
          </>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {!deleteMode && (
            <TanStackTable
              data={rowData}
              columns={columns}
              onRowClick={handleRowClick}
              getRowClassName={getRowClassName}
              enablePagination={true}
              pagination={pagination}
              onPaginationChange={(newPage, newLimit) => {
                console.log('Inventory pagination change requested:', newPage, newLimit);
                setPagination(prev => ({...prev, page: newPage, limit: newLimit}));
                fetchInventory({page: newPage, limit: newLimit});
              }}
              searchQuery={searchQuery}
              onSearchChange={(search) => {
                setSearchQuery(search);
                setPagination(prev => ({...prev, page: 1}));
                fetchInventory({search, page: 1});
              }}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={(sortBy, sortOrder) => {
                setSortBy(sortBy);
                setSortOrder(sortOrder);
                fetchInventory({sortBy, sortOrder});
              }}
            />
          )}
          {deleteMode && (
            <Box>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Select</TableCell>
                    <TableCell>ID</TableCell>
                    <TableCell>Product Name</TableCell>
                    <TableCell>Quantity On Hand</TableCell>
                    <TableCell>Minimum Stock Level</TableCell>
                    <TableCell>Maximum Stock Level</TableCell>
                    <TableCell>Reorder Point</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rowData.map((inventory) => (
                    <TableRow key={inventory.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedInventory.includes(inventory.id)}
                          onChange={() => {
                            setSelectedInventory((prev) =>
                              prev.includes(inventory.id)
                                ? prev.filter((id) => id !== inventory.id)
                                : [...prev, inventory.id]
                            );
                          }}
                        />
                      </TableCell>
                      <TableCell>{inventory.id}</TableCell>
                      <TableCell>{inventory.product.product_name}</TableCell>
                      <TableCell>{inventory.quantity_on_hand}</TableCell>
                      <TableCell>{inventory.minimum_stock_level}</TableCell>
                      <TableCell>{inventory.maximum_stock_level}</TableCell>
                      <TableCell>{inventory.reorder_point}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default InventoryList;
